import type { RoomModeratorRaw } from 'api/types';
import type { RoomModerator } from '../types';
import { determineModeratorType } from './determineModeratorType';

const roleFromRaw = (m: RoomModeratorRaw): RoomModerator['role'] => {
    if (m.moderatorType === 'PRIMARY') return 'main';
    if (m.moderatorType === 'SECONDARY') return 'moderator';
    return m.role === 'main' ? 'main' : 'moderator';
};

/**
 * Валидирует и парсит модератора типа USER
 */
const parseUserModerator = (moderator: RoomModeratorRaw): RoomModerator | null => {
    if (typeof moderator.userId !== 'string' || moderator.userId.trim() === '') {
        console.warn('[Moderators] невалидный USER модератор: userId обязателен и не может быть пустой строкой', {
            moderator,
        });
        return null;
    }

    return {
        type: 'USER',
        userId: moderator.userId,
        identity: null,
        role: roleFromRaw(moderator),
    };
};

/**
 * Валидирует и парсит модератора типа GUEST (по identity)
 */
const parseGuestModerator = (moderator: RoomModeratorRaw): RoomModerator | null => {
    const identity =
        typeof moderator.identity === 'string' && moderator.identity.trim() !== '' ? moderator.identity.trim() : null;
    if (!identity) {
        console.warn('[Moderators] невалидный GUEST модератор: нужен identity', { moderator });
        return null;
    }

    return {
        type: 'GUEST',
        userId: null,
        identity,
        role: roleFromRaw(moderator),
    };
};

/**
 * Парсит один элемент модератора
 */
const parseModeratorItem = (item: unknown): RoomModerator | null => {
    if (!item || typeof item !== 'object') {
        console.warn('[Moderators] невалидный тип модератора', { item });
        return null;
    }

    const moderator = item as RoomModeratorRaw;

    // определяем тип
    const type = determineModeratorType(moderator);
    if (!type) {
        console.warn('[Moderators] не удалось определить тип модератора', { moderator });
        return null;
    }

    switch (type) {
        case 'USER':
            return parseUserModerator(moderator);
        case 'GUEST':
            return parseGuestModerator(moderator);
        default:
            console.warn('[Moderators] неизвестный тип', { type, moderator });
            return null;
    }
};

/**
 * Парсит и валидирует массив модераторов из метаданных комнаты.
 * Роли берём только из элементов (moderatorType/role) через roleFromRaw
 * Главный модератор определяется сервером (moderatorType === 'PRIMARY');
 */
export const parseModeratorsFromMetadata = (
    metadata: { moderators?: RoomModeratorRaw[] } | null | undefined,
): RoomModerator[] => {
    if (!metadata?.moderators) return [];

    if (!Array.isArray(metadata.moderators)) {
        console.warn('[Moderators] модераторы не являются массивом', { moderators: metadata.moderators });
        return [];
    }

    return metadata.moderators.reduce<RoomModerator[]>((result, item) => {
        const moderator = parseModeratorItem(item);
        if (moderator !== null) {
            result.push(moderator);
        }
        return result;
    }, []);
};
